import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';
import { PlanProvider } from './context/PlanContext';
import { PillarProvider } from './context/PillarContext';
import PlanErrorInterceptor from './components/PlanErrorInterceptor';
import { Toaster } from './components/ui/sonner';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import AuthCallback from './pages/auth/AuthCallback';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import AccountDetail from './pages/AccountDetail';
import Contacts from './pages/Contacts';
import ContactDetail from './pages/ContactDetail';
import EventOutreachStudio from './pages/EventOutreachStudio';
import Pipeline from './pages/Pipeline';
import LeadPipeline from './pages/LeadPipeline';
import DealDetail from './pages/DealDetail';
import DealRegistration from './pages/DealRegistration';
import Proposals from './pages/Proposals';
import ProposalCreator from './pages/ProposalCreator';
import AIAssistant from './pages/AIAssistant';
import Activities from './pages/Activities';
import Communications from './pages/Communications';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import DealRooms from './pages/DealRooms';
import DealRoomDetail from './pages/DealRoomDetail';
import PublicDealRoom from './pages/PublicDealRoom';
import Admin from './pages/Admin';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import EmailSequences from './pages/EmailSequences';
import SequenceEditor from './pages/SequenceEditor';
import SequenceMonitor from './pages/SequenceMonitor';
import ListManagement from './pages/ListManagement';
import Inbox from './pages/Inbox';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import LandingPage from './pages/LandingPage';
import DemoLogin from './pages/auth/DemoLogin';
import JoinPage from './pages/auth/JoinPage';
import SSOComplete from './pages/auth/SSOComplete';
import PendingApproval from './pages/auth/PendingApproval';
import VerifyEmail from './pages/auth/VerifyEmail';
import ResetPassword from './pages/auth/ResetPassword';
import BookingLinks from './pages/BookingLinks';
import PublicBooking from './pages/PublicBooking';
import Forms from './pages/Forms';
import FormBuilder from './pages/FormBuilder';
import FormFlows from './pages/FormFlows';
import FormFlowBuilder from './pages/FormFlowBuilder';
import CoachBookings from './pages/CoachBookings';
import PublicForm from './pages/PublicForm';
import PublicFlow from './pages/PublicFlow';
import PublicReschedule from './pages/PublicReschedule';
import PublicUnsubscribe from './pages/PublicUnsubscribe';
import AIHeartbeat from './pages/AIHeartbeat';
import FeatureRequests from './pages/FeatureRequests';
import Campaigns from './pages/Campaigns';
import CampaignBuilder from './pages/CampaignBuilder';
import CampaignExecutionReport from './pages/CampaignExecutionReport';
import Events from './pages/Events';
import EventDetail from './pages/EventDetail';
import Onboarding from './pages/Onboarding';
import ReleaseNotes from './pages/ReleaseNotes';
import ContentLibrary from './pages/ContentLibrary';
import Tasks from './pages/Tasks';
import Wiki from './pages/Wiki';
import Affiliates from './pages/Affiliates';
import Testimonials from './pages/Testimonials';
import HelpGuide from './pages/HelpGuide';
import TestimonialBuilder from './pages/TestimonialBuilder';
import PublicTestimonial from './pages/PublicTestimonial';
import PublicTestimonialView from './pages/PublicTestimonialView';
import TestimonialWall from './pages/TestimonialWall';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import AIPolicy from './pages/legal/AIPolicy';
import DataPolicy from './pages/legal/DataPolicy';
import TermsAndConditions from './pages/legal/TermsAndConditions';
import Sites from './pages/Sites';
import SiteBuilder from './pages/SiteBuilder';
import AgenticReviews from './pages/AgenticReviews';
import Agents from './pages/Agents';
import AgentBuilder from './pages/AgentBuilder';
import PlaybookList from './pages/PlaybookList';
import PlaybookBuilder from './pages/PlaybookBuilder';
import MeetingSessions from './pages/MeetingSessions';
import MeetingSessionDetail from './pages/MeetingSessionDetail';
import DesktopApp from './pages/DesktopApp';
// Practice / Role Play (v2.16 redesign)
import PracticeHome from './pages/practice/PracticeHome';
import RolePlaySetup from './pages/practice/RolePlaySetup';
import RolePlayLive from './pages/practice/RolePlayLive';
import RolePlaySessions from './pages/practice/RolePlaySessions';
import RolePlaySessionDetail from './pages/practice/RolePlaySessionDetail';
import MicroPractice from './pages/practice/MicroPractice';
import Leaderboard from './pages/practice/Leaderboard';
import AvatarLibrary from './pages/admin/AvatarLibrary';
import ATSDashboard from './pages/ats/ATSDashboard';
import ATSJobDetail from './pages/ats/ATSJobDetail';
import ATSTalentPool from './pages/ats/ATSTalentPool';
import ATSAgentQueue from './pages/ats/ATSAgentQueue';
import ATSCVReview from './pages/ats/ATSCVReview';
import ATSApplicantDetail from './pages/ats/ATSApplicantDetail';
import ATSSettings from './pages/ats/ATSSettings';
import ATSReports from './pages/ats/ATSReports';
import ATSActivationCampaigns from './pages/ats/ATSActivationCampaigns';
import ATSMatchingWorkspace from './pages/ats/ATSMatchingWorkspace';
import CareersPage from './pages/public/CareersPage';
import JobDetailPage from './pages/public/JobDetailPage';
import ApplicantPortal from './pages/public/ApplicantPortal';
import ClientPortal from './pages/public/ClientPortal';
import StarFeatureModal from './components/StarFeatureModal';
// Hidden 2026-04-23 — AI Assistant orb + MiniChat needs a rework pass
// (see BACKLOG AIO-01). Leaving the import + render commented so the
// codepath comes back in one edit when we pick it up.
// import FloatingAI from './components/ai-entity/FloatingAI';
import { PromptForgeProvider, defaultSubmit } from '@multiplai_tech/prompt-forge';
import '@multiplai_tech/prompt-forge/theme.css';
import './App.css';

// Prompt Forge: ⌘⇧P opens modal, ⌥-click pins any element and generates
// a structured Claude Code prompt. Mounted for every authenticated user,
// gated to the `allowedMCPUsers` whitelist for direct-send to Claude.
//
// Screenshot capture: html2canvas is dynamically imported the first time a
// user asks for a screenshot so it stays out of the initial bundle
// (~170 KB gzipped). Failures are non-fatal — the payload still submits
// without the attachment.
const capturePageScreenshot = async () => {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        backgroundColor: null,
        // Cap the render so we don't generate a massive base64 blob on
        // ultra-wide displays; the dataURL is embedded in the payload.
        scale: Math.min(window.devicePixelRatio || 1, 2),
    });
    return canvas.toDataURL('image/png');
};

// Route `fr` / `bug` submissions to the existing /api/feature-requests
// endpoint, fall back to the package's defaultSubmit for `mcp` / `draft`.
// Returns { kind, id, label, url } so the modal's success state links back
// to the created ticket.
async function handlePromptForgeSubmit(payload, target) {
    if (target === 'fr' || target === 'bug') {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const token = localStorage.getItem('crm_token');
        // Derive a reasonable title from the first markdown heading in the
        // generated prompt; fallback to the page id + mode so we always send
        // something that passes the >=5-char validation.
        const firstHeading = (payload.prompt || '').split('\n').find(l => l.startsWith('#'));
        const derivedTitle = (firstHeading || '')
            .replace(/^#+\s*/, '')
            .trim()
            .slice(0, 120);
        const fallback = `Prompt Forge — ${payload.pageId || 'unknown'} (${target})`;
        const title = derivedTitle.length >= 5 ? derivedTitle : fallback;

        const res = await fetch(`${API}/feature-requests/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                type: target === 'bug' ? 'bug' : 'feature',
                title,
                description: payload.prompt || '',
                page_url: typeof window !== 'undefined' ? window.location.href : '',
                attachments: payload.withScreenshot && payload.screenshot ? [payload.screenshot] : [],
            }),
        });
        if (!res.ok) {
            const detail = await res.text().catch(() => '');
            throw new Error(`Feature request submit failed (${res.status}): ${detail}`);
        }
        const doc = await res.json();
        const frontend = (typeof window !== 'undefined' ? window.location.origin : '');
        return {
            kind: target === 'bug' ? 'BUG' : 'FR',
            id: doc.ticket_number || doc.request_id,
            label: doc.ticket_number || 'Ticket',
            url: `${frontend}/feature-requests?ticket=${encodeURIComponent(doc.ticket_number || doc.request_id || '')}`,
        };
    }
    return defaultSubmit(payload, target, { mcpEndpoint: '/api/claude-mcp' });
}

function PromptForgeBridge({ children }) {
    const { user } = useAuth();
    // Render children without the provider when there's no user yet —
    // the bridge can't supply a valid reporter identity, and the login /
    // landing pages don't need the hotkey anyway.
    if (!user?.email) return children;
    return (
        <PromptForgeProvider
            user={{ email: user.email, name: user.name || user.email }}
            githubRepo="multiplai-tech/ai-crm-36"
            mcpEndpoint="/api/claude-mcp"
            allowedMCPUsers={['matt@aicoaches.com', 'matt@multiplai.tech', 'matt@kesby.land']}
            defaultMode="inferred"
            captureScreenshot={capturePageScreenshot}
            onSubmit={handlePromptForgeSubmit}
        >
            {children}
        </PromptForgeProvider>
    );
}

class ErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { hasError: false, error: null }; }
    static getDerivedStateFromError(error) { return { hasError: true, error }; }
    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
                    <h2 style={{ color: '#dc2626', marginBottom: 8 }}>Something went wrong</h2>
                    <pre style={{ background: '#fef2f2', padding: 16, borderRadius: 8, fontSize: 12, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                        {this.state.error?.message || 'Unknown error'}
                        {'\n\n'}
                        {this.state.error?.stack?.split('\n').slice(0, 5).join('\n')}
                    </pre>
                    <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', background: '#1c1917', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>
                        Reload Page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

function ReconnectingScreen() {
    useEffect(() => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const interval = setInterval(async () => {
            try {
                const res = await fetch(`${backendUrl}/api/health`, { method: 'GET', cache: 'no-store' });
                if (res.ok) window.location.reload();
            } catch (_) { /* still down */ }
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
            <div className="text-center max-w-sm">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h2 className="text-lg font-semibold text-stone-800 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Updating with new features...</h2>
                <p className="text-sm text-stone-500 mb-2">The system is being updated. This usually takes less than a minute.</p>
                <p className="text-xs text-stone-400 mb-4">The page will automatically reload when ready.</p>
                <div className="w-8 h-8 border-3 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 text-sm font-medium text-stone-600 bg-stone-100 rounded-lg hover:bg-stone-200"
                >
                    Retry Now
                </button>
            </div>
        </div>
    );
}

function ProtectedRoute({ children }) {
    const { user, loading, token } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500 text-sm">Loading CRM...</p>
                </div>
            </div>
        );
    }

    // Token exists but user is null → backend was temporarily unavailable (e.g. deploy in progress)
    // Show a retry screen instead of kicking to login
    if (!user && (token || localStorage.getItem('crm_token'))) {
        return <ReconnectingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Pending approval users can only see the pending page
    if (user.approval_status === 'pending' && location.pathname !== '/pending-approval') {
        return <Navigate to="/pending-approval" replace />;
    }

    return <AppLayout><ErrorBoundary>{children}</ErrorBoundary></AppLayout>;
}

function AdminRoute({ children }) {
    const { user, loading, token } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen bg-stone-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-stone-500 text-sm">Loading CRM...</p>
                </div>
            </div>
        );
    }

    if (!user && (token || localStorage.getItem('crm_token'))) {
        return <ReconnectingScreen />;
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!user.is_admin) {
        return <Navigate to="/dashboard" replace />;
    }

    return <AppLayout><ErrorBoundary>{children}</ErrorBoundary></AppLayout>;
}

function AppRouter() {
    const location = useLocation();

    // Detect session_id synchronously during render (prevents race conditions)
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }

    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/demo" element={<DemoLogin />} />
            <Route path="/join/:token" element={<JoinPage />} />
            <Route path="/sso/complete" element={<SSOComplete />} />
            <Route path="/pending-approval" element={<PendingApproval />} />
            <Route path="/room/:slug" element={<PublicDealRoom />} />
            <Route path="/book/:slug" element={<PublicBooking />} />
            <Route path="/form/:slug" element={<PublicForm />} />
            <Route path="/flow/:slug" element={<PublicFlow />} />
            <Route path="/reschedule/:bookingId" element={<PublicReschedule />} />
            <Route path="/unsubscribe" element={<PublicUnsubscribe />} />
            <Route path="/testimonial/:slug/:token" element={<PublicTestimonial />} />
            <Route path="/testimonial/view/:submissionId" element={<PublicTestimonialView />} />
            <Route path="/testimonials/wall/:orgSlug" element={<TestimonialWall />} />
            <Route path="/careers/:orgSlug" element={<CareersPage />} />
            <Route path="/careers/:orgSlug/jobs/:jobId" element={<JobDetailPage />} />
            <Route path="/applicant-portal" element={<ApplicantPortal />} />
            <Route path="/client-portal" element={<ClientPortal />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/ai-policy" element={<AIPolicy />} />
            <Route path="/data-policy" element={<DataPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/companies" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
            <Route path="/companies/:accountId" element={<ProtectedRoute><AccountDetail /></ProtectedRoute>} />
            <Route path="/accounts" element={<Navigate to="/companies" replace />} />
            <Route path="/accounts/:accountId" element={<ProtectedRoute><AccountDetail /></ProtectedRoute>} />
            <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
            <Route path="/contacts/:contactId" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
            <Route path="/contacts/:contactId/event-outreach" element={<ProtectedRoute><EventOutreachStudio /></ProtectedRoute>} />
            <Route path="/pipeline" element={<ProtectedRoute><Pipeline /></ProtectedRoute>} />
            <Route path="/lead-pipeline" element={<ProtectedRoute><LeadPipeline /></ProtectedRoute>} />
            <Route path="/deals/:dealId" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
            <Route path="/deal-registration" element={<ProtectedRoute><DealRegistration /></ProtectedRoute>} />
            <Route path="/deal-rooms" element={<ProtectedRoute><DealRooms /></ProtectedRoute>} />
            <Route path="/deal-rooms/:roomId" element={<ProtectedRoute><DealRoomDetail /></ProtectedRoute>} />
            <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
            <Route path="/proposals/new" element={<ProtectedRoute><ProposalCreator /></ProtectedRoute>} />
            <Route path="/proposals/:proposalId" element={<ProtectedRoute><ProposalCreator /></ProtectedRoute>} />
            <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
            <Route path="/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
            <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
            <Route path="/booking-links" element={<ProtectedRoute><BookingLinks /></ProtectedRoute>} />
            <Route path="/forms" element={<ProtectedRoute><Forms /></ProtectedRoute>} />
            <Route path="/forms/new/edit" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
            <Route path="/forms/:formId/edit" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
            <Route path="/form-flows" element={<ProtectedRoute><FormFlows /></ProtectedRoute>} />
            <Route path="/form-flows/:flowId/edit" element={<ProtectedRoute><FormFlowBuilder /></ProtectedRoute>} />
            <Route path="/bookings" element={<ProtectedRoute><CoachBookings /></ProtectedRoute>} />
            <Route path="/ai-heartbeat" element={<AdminRoute><AIHeartbeat /></AdminRoute>} />
            <Route path="/ai-reviews" element={<ProtectedRoute><AgenticReviews /></ProtectedRoute>} />
            <Route path="/feature-requests" element={<ProtectedRoute><FeatureRequests /></ProtectedRoute>} />
            <Route path="/release-notes" element={<ProtectedRoute><ReleaseNotes /></ProtectedRoute>} />
            <Route path="/content-library" element={<ProtectedRoute><ContentLibrary /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/wiki" element={<ProtectedRoute><Wiki /></ProtectedRoute>} />
            <Route path="/affiliates" element={<ProtectedRoute><Affiliates /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpGuide /></ProtectedRoute>} />
            <Route path="/testimonials" element={<ProtectedRoute><Testimonials /></ProtectedRoute>} />
            <Route path="/testimonials/new/edit" element={<ProtectedRoute><TestimonialBuilder /></ProtectedRoute>} />
            <Route path="/testimonials/:requestId/edit" element={<ProtectedRoute><TestimonialBuilder /></ProtectedRoute>} />
            <Route path="/communications" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
            <Route path="/products/:productId" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
            <Route path="/email-sequences" element={<ProtectedRoute><EmailSequences /></ProtectedRoute>} />
            <Route path="/email-sequences/:sequenceId/edit" element={<ProtectedRoute><SequenceEditor /></ProtectedRoute>} />
            <Route path="/email-sequences/:sequenceId/monitor" element={<ProtectedRoute><SequenceMonitor /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
            <Route path="/campaigns/:campaignId/edit" element={<ProtectedRoute><CampaignBuilder /></ProtectedRoute>} />
            <Route path="/campaigns/:campaignId/execution" element={<ProtectedRoute><CampaignExecutionReport /></ProtectedRoute>} />
            <Route path="/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
            <Route path="/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
            <Route path="/sites" element={<ProtectedRoute><Sites /></ProtectedRoute>} />
            <Route path="/sites/:siteId/edit" element={<ProtectedRoute><SiteBuilder /></ProtectedRoute>} />
            <Route path="/sites/:siteId/pages/:pageId/edit" element={<ProtectedRoute><SiteBuilder /></ProtectedRoute>} />
            {/* ATS / Recruitment */}
            <Route path="/recruitment" element={<ProtectedRoute><ATSDashboard /></ProtectedRoute>} />
            <Route path="/recruitment/jobs" element={<ProtectedRoute><ATSDashboard /></ProtectedRoute>} />
            <Route path="/recruitment/jobs/:jobId" element={<ProtectedRoute><ATSJobDetail /></ProtectedRoute>} />
            <Route path="/recruitment/talent-pool" element={<ProtectedRoute><ATSTalentPool /></ProtectedRoute>} />
            <Route path="/recruitment/agent-queue" element={<ProtectedRoute><ATSAgentQueue /></ProtectedRoute>} />
            <Route path="/recruitment/cv-review" element={<ProtectedRoute><ATSCVReview /></ProtectedRoute>} />
            <Route path="/recruitment/talent-pool/:applicantId" element={<ProtectedRoute><ATSApplicantDetail /></ProtectedRoute>} />
            <Route path="/recruitment/settings" element={<ProtectedRoute><ATSSettings /></ProtectedRoute>} />
            <Route path="/recruitment/reports" element={<ProtectedRoute><ATSReports /></ProtectedRoute>} />
            <Route path="/recruitment/activation" element={<ProtectedRoute><ATSActivationCampaigns /></ProtectedRoute>} />
            <Route path="/recruitment/matching" element={<ProtectedRoute><ATSMatchingWorkspace /></ProtectedRoute>} />
            <Route path="/agents" element={<ProtectedRoute><Agents /></ProtectedRoute>} />
            <Route path="/agents/:agentId" element={<ProtectedRoute><AgentBuilder /></ProtectedRoute>} />
            {/* Sales Meeting Copilot */}
            <Route path="/playbooks" element={<AdminRoute><PlaybookList /></AdminRoute>} />
            <Route path="/playbooks/:playbookId" element={<AdminRoute><PlaybookBuilder /></AdminRoute>} />
            <Route path="/meeting-sessions" element={<ProtectedRoute><MeetingSessions /></ProtectedRoute>} />
            <Route path="/meeting-sessions/:sessionId" element={<ProtectedRoute><MeetingSessionDetail /></ProtectedRoute>} />
            {/* Practice / Role Play */}
            <Route path="/practice" element={<ProtectedRoute><PracticeHome /></ProtectedRoute>} />
            <Route path="/practice/role-play" element={<ProtectedRoute><RolePlaySessions /></ProtectedRoute>} />
            <Route path="/practice/role-play/new" element={<ProtectedRoute><RolePlaySetup /></ProtectedRoute>} />
            <Route path="/practice/role-play/live/:sessionId" element={<ProtectedRoute><RolePlayLive /></ProtectedRoute>} />
            <Route path="/practice/role-play/sessions/:sessionId" element={<ProtectedRoute><RolePlaySessionDetail /></ProtectedRoute>} />
            <Route path="/micro-practice" element={<ProtectedRoute><MicroPractice /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/admin/role-play/avatars" element={<AdminRoute><AvatarLibrary /></AdminRoute>} />
            <Route path="/desktop-app" element={<ProtectedRoute><DesktopApp /></ProtectedRoute>} />
            <Route path="/list-management" element={<AdminRoute><ListManagement /></AdminRoute>} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="/super-admin" element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 2 * 60 * 1000,      // 2 min — data considered fresh
            gcTime: 10 * 60 * 1000,         // 10 min — cache retained after unmount
            refetchOnWindowFocus: true,
            retry: 1,
        },
    },
});

function App() {
    useEffect(() => {
        const remove = () => {
            // Remove Emergent badge by ID
            const badge = document.getElementById('emergent-badge');
            if (badge) badge.remove();
            // Remove any Emergent-injected scripts
            document.querySelectorAll('script[src*="emergent"]').forEach(el => el.remove());
            // Remove any Emergent-injected iframes or widgets
            document.querySelectorAll('iframe[src*="emergent"], div[id*="emergent"], div[class*="emergent"]').forEach(el => el.remove());
            // Remove any fixed-position widget injected at bottom-right (common pattern for third-party badges)
            document.querySelectorAll('a[href*="emergent"], a[href*="emergentagent"]').forEach(el => el.remove());
        };
        remove();
        // Also watch for it being re-injected
        const observer = new MutationObserver(remove);
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <AuthProvider>
                    <PermissionsProvider>
                        <PlanProvider>
                            <PillarProvider>
                                <PromptForgeBridge>
                                    <AppRouter />
                                </PromptForgeBridge>
                                <PlanErrorInterceptor />
                                <StarFeatureModal />
                                {/* <FloatingAI />  — disabled 2026-04-23; see BACKLOG AIO-01 */}
                                <Toaster position="top-right" richColors />
                            </PillarProvider>
                        </PlanProvider>
                    </PermissionsProvider>
                </AuthProvider>
            </BrowserRouter>
            {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
    );
}

export default App;
